// src/redux/reducers/errorLog.reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchSpaHistory } from '../actions/spaHistory.actions';
import SingleState from '../states/single.state';

const initialState: SingleState = {
  loading: false,
  partialLoading: false,
  loadingProgress: 0,
  lastQuery: null,
  data: [],
  error: null,
};

// Mirror of the backend's per-page de-dupe, applied once across the full
// assembled range: sort by timestamp and keep a row only when a tracked value
// changed from the previously kept row. This removes the duplicate rows that
// survive where two backend pages meet.
const dedupeHistory = (items: any[]): any[] => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const sorted = [...items].sort((a: any, b: any) => {
    const ta = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
    return ta - tb;
  });

  const deduped: any[] = [];
  let lastValues: Record<string, any> | null = null;

  for (const item of sorted) {
    let changed = false;
    const currentValues: Record<string, any> = {};

    for (const key of Object.keys(item)) {
      if (key === 'timestamp' || key === 'spa_guid') continue;
      currentValues[key] = item[key];
      if (!lastValues || lastValues[key] !== item[key]) {
        changed = true;
      }
    }

    if (changed || deduped.length === 0) {
      deduped.push(item);
      lastValues = currentValues;
    }
  }

  return deduped;
};

const SpaHistorySlice = createSlice({
  name: 'spa-history',
  initialState,
  reducers: {
    // Progress (0–100) reported by the fetch thunk after each page. The data is
    // buffered in the thunk and committed once at the end via setSpaHistory, so
    // the graph/table render a single time instead of re-rendering per chunk.
    setSpaHistoryProgress: (state, action: PayloadAction<number>) => {
      state.loadingProgress = action.payload;
    },
    setSpaHistory: (state, action: PayloadAction<any[]>) => {
      state.data = dedupeHistory(action.payload);
      state.loading = false;
      state.partialLoading = false;
      state.loadingProgress = 100;
    },
    clearSpaHistory: (state) => {
      state.data = [];
      state.loading = false;
      state.partialLoading = false;
      state.loadingProgress = 0;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSpaHistory.pending, (state, action) => {
        state.loading = true;
        state.partialLoading = true;
        state.loadingProgress = 0;
        state.data = [];
        state.error = null;
        // Signature of the report being fetched, so a view that already holds this
        // exact data (e.g. toggling table/graph) can skip refetching it.
        state.lastQuery = JSON.stringify(action.meta.arg);
      })
      .addCase(fetchSpaHistory.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.partialLoading = false;
      })
      .addCase(fetchSpaHistory.rejected, (state, action) => {
        state.loading = false;
        state.partialLoading = false;
        state.loadingProgress = 0;
        state.lastQuery = null; // allow a retry of the same query after a failure
        state.error = action.payload || 'An error occurred';
      });
  },
});

export const { setSpaHistoryProgress, setSpaHistory, clearSpaHistory } = SpaHistorySlice.actions;

export default SpaHistorySlice.reducer;
